import React, { useEffect, useMemo, useState } from "react";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import SessionCard from "@/components/SessionCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Star, Funnel, Heart, PlayCircle, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FeaturedQACard } from "@/components/FeaturedQACard";
import NowShowingWidget from "@/components/NowShowingWidget";
import { isConferenceOver } from "@/lib/conference";
import { VIRTUAL_PLATFORM } from "@/lib/virtual";

const DAYS = [
  { id: "2026-06-22", label: "Mon 22", short: "Day 1" },
  { id: "2026-06-23", label: "Tue 23", short: "Day 2" },
  { id: "2026-06-24", label: "Wed 24", short: "Day 3" },
];
const STAGES = ["All", "Plenary", "Concurrent 1", "Concurrent 2", "Concurrent 3", "Exhibition Hall"];

export default function Program() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [stageLabels, setStageLabels] = useState({});
  const [liveStreams, setLiveStreams] = useState({});
  const [favorites, setFavorites] = useState(new Set());
  // Default to today if today is one of the conference days; otherwise Day 3.
  // Day 3 is the most recent / current day during the back-half of the week.
  const today = new Date().toISOString().slice(0, 10);
  const todayDay = DAYS.find((d) => d.id === today);
  const [day, setDay] = useState(todayDay ? todayDay.id : DAYS[2].id);
  const [stage, setStage] = useState("All");
  const [myAgendaOnly, setMyAgendaOnly] = useState(false);

  useEffect(() => {
    const load = () => {
      api.get("/sessions").then((r) => setSessions(r.data)).catch((err) => {
        console.warn("[sessions] load failed:", err);
        setSessions([]);
      });
    };
    load();
    api.get("/info").then((r) => {
      setStageLabels(r.data?.stage_labels || {});
      setLiveStreams(r.data?.live_streams || {});
    }).catch(() => {});
    // Re-fetch sessions when the tab becomes visible again. Catches the case
    // where an admin reorders sessions in another tab/window and the delegate
    // returns to the app — without this they'd see the stale on-mount snapshot
    // until a hard refresh.
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", load);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", load);
    };
  }, []);
  useEffect(() => {
    if (user) api.get("/favorites").then((r) => setFavorites(new Set(r.data))).catch((err) => {
      console.warn("[favorites] load failed:", err);
    });
  }, [user]);

  const toggleFav = async (sid) => {
    if (!user) { toast.info("Sign in to star sessions for your agenda."); return; }
    const next = new Set(favorites);
    if (next.has(sid)) {
      next.delete(sid);
      setFavorites(next);
      try { await api.delete(`/favorites/${sid}`); }
      catch (err) { console.warn("[favorites] remove failed:", err); }
    } else {
      next.add(sid);
      setFavorites(next);
      try { await api.post(`/favorites/${sid}`); }
      catch (err) { console.warn("[favorites] add failed:", err); }
    }
  };

  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      if (s.date !== day) return false;
      if (stage !== "All" && s.stage !== stage) return false;
      if (myAgendaOnly && !favorites.has(s.id)) return false;
      return true;
    });
  }, [sessions, day, stage, myAgendaOnly, favorites]);

  // Group by start_time, then within each time slot sub-group by (stage, track) so
  // that e.g. all 3 talks in "Keynote 1: Basics" appear under one block heading.
  // Items inside each block are explicitly sorted by `sort_order` (with id as a
  // stable tiebreaker) so admin reorder always wins, regardless of how the API
  // happens to return the rows.
  const grouped = useMemo(() => {
    const byTime = new Map();
    filtered.forEach((s) => {
      if (!byTime.has(s.start_time)) byTime.set(s.start_time, []);
      byTime.get(s.start_time).push(s);
    });
    return [...byTime.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([time, rows]) => {
        const blockMap = new Map();
        rows.forEach((s) => {
          const blockKey = `${s.stage}|${s.track || ""}`;
          if (!blockMap.has(blockKey)) blockMap.set(blockKey, { stage: s.stage, track: s.track, stage_label: s.stage_label, items: [] });
          blockMap.get(blockKey).items.push(s);
        });
        for (const block of blockMap.values()) {
          block.items.sort((a, b) => {
            const ao = a.sort_order ?? 0;
            const bo = b.sort_order ?? 0;
            if (ao !== bo) return ao - bo;
            return (a.id || "").localeCompare(b.id || "");
          });
        }
        return { time, blocks: [...blockMap.values()] };
      });
  }, [filtered]);

  return (
    <div data-testid="program-page">
      <div className="mb-4">
        <div className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest mb-1">22-24 June · SEC Glasgow</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Programme</h1>
      </div>

      {isConferenceOver() && (
        <div
          data-testid="thanks-banner"
          className="mb-4 p-4 bg-gradient-to-br from-[#43C4E3] to-[#0099B8] text-[#FFFFFF] rounded-sm border border-[#000000]/10"
        >
          <div className="flex items-start gap-3">
            <Heart size={22} weight="fill" className="text-[#FFFFFF] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg leading-tight">Thanks for coming to DFTB26.</div>
              <div className="text-sm text-[#FFFFFF]/90 mt-1 leading-relaxed">
                Replays, slides and Q&amp;A from the week are all here. See you at DFTB27.
              </div>
            </div>
          </div>
        </div>
      )}

      {isConferenceOver() && (
        <a
          href={VIRTUAL_PLATFORM.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="program-virtual-cta"
          className="mb-4 block p-4 bg-[#000000] text-[#FFFFFF] rounded-sm border border-[#000000] hover:bg-[#1c1c24] transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 grid place-items-center rounded-sm bg-[#43C4E3]/15 text-[#43C4E3] flex-shrink-0">
              <PlayCircle size={24} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#43C4E3]">
                New · DFTB26 Virtual Platform
              </div>
              <div className="font-display text-base sm:text-lg leading-tight mt-0.5">
                {VIRTUAL_PLATFORM.headline}
              </div>
              <div className="text-xs text-[#FFFFFF]/80 mt-0.5">
                {VIRTUAL_PLATFORM.subline}
              </div>
            </div>
            <span className="font-mono-time text-xs uppercase tracking-widest text-[#43C4E3] flex items-center gap-1 flex-shrink-0">
              Watch <ArrowSquareOut size={12} weight="bold" />
            </span>
          </div>
        </a>
      )}

      <FeaturedQACard />

      <NowShowingWidget sessions={sessions} liveStreamStages={liveStreams} stageLabels={stageLabels} />

      <Tabs value={day} onValueChange={setDay} data-testid="program-day-tabs">
        <TabsList className="bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm h-auto p-1 w-full grid grid-cols-3">
          {DAYS.map((d) => (
            <TabsTrigger
              key={d.id} value={d.id} data-testid={`day-tab-${d.short.toLowerCase().replace(" ", "-")}`}
              className="rounded-sm py-2.5 data-[state=active]:bg-[#000000] data-[state=active]:text-[#FFFFFF] data-[state=active]:shadow-none font-medium"
            >
              <span className="flex flex-col items-center gap-0">
                <span className="font-mono-time text-[10px] uppercase opacity-80">{d.short}</span>
                <span>{d.label}</span>
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          <Funnel size={16} className="text-[#7A828C] flex-shrink-0" />
          {STAGES.map((st) => (
            <button
              key={st}
              onClick={() => setStage(st)}
              data-testid={`stage-chip-${st.toLowerCase().replace(" ", "-")}`}
              className={`flex-shrink-0 h-9 px-3 rounded-sm text-xs font-medium uppercase tracking-wider transition-colors border ${
                stage === st ? "bg-[#1c1c24] text-[#FFFFFF] border-[#1c1c24]" : "bg-transparent text-[#5a5a5a] border-[#E5E9F0] hover:border-[#000000]"
              }`}
            >
              {st === "All" ? "All" : (stageLabels[st] || st)}
            </button>
          ))}
          {user && (
            <button
              onClick={() => setMyAgendaOnly((v) => !v)}
              data-testid="my-agenda-toggle"
              className={`flex-shrink-0 h-9 px-3 rounded-sm text-xs font-medium uppercase tracking-wider transition-colors border flex items-center gap-1.5 ${
                myAgendaOnly ? "bg-[#000000] text-[#FFFFFF] border-[#000000]" : "bg-transparent text-[#000000] border-[#000000]/40 hover:border-[#000000]"
              }`}
            >
              <Star size={14} weight={myAgendaOnly ? "fill" : "regular"} /> My Agenda
            </button>
          )}
        </div>

        {DAYS.map((d) => (
          <TabsContent key={d.id} value={d.id} className="mt-4 space-y-6">
            {grouped.length === 0 ? (
              <div className="text-center py-12 text-[#7A828C]" data-testid="program-empty">
                No sessions match these filters.
              </div>
            ) : (
              grouped.map(({ time, blocks }) => (
                <div key={time} className="relative">
                  <div className="sticky top-14 z-10 -mx-4 sm:mx-0 px-4 sm:px-0 py-2 bg-[#FFFFFF]/90 backdrop-blur flex items-center gap-3">
                    <span className="font-mono-time text-xs tracking-widest text-[#000000]">{time}</span>
                    <span className="h-px flex-1 bg-[#E5E9F0]" />
                  </div>
                  <div className="space-y-5 mt-2">
                    {blocks.map((block, bi) => {
                      const isBreak = (block.track || "").toLowerCase() === "break";
                      const showHeading = !!block.track && !isBreak;
                      return (
                        <div key={bi} className="space-y-2" data-testid={`program-block-${time}-${bi}`}>
                          {showHeading && (
                            <div className="px-1">
                              <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#000000] mb-0.5" data-testid={`program-block-time-${time}-${bi}`}>
                                {block.items[0].start_time}{block.items[0].end_time ? `-${block.items[0].end_time}` : ""}
                              </div>
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">
                                  {block.stage_label || block.stage}
                                </span>
                                <h2 className="font-display text-base sm:text-lg text-[#1c1c24] leading-tight" data-testid={`program-block-title-${time}-${bi}`}>
                                  {block.track}
                                  {block.items?.[0]?.chair && (
                                    <span className="text-[#7A828C]" data-testid={`program-block-chair-${time}-${bi}`}>
                                      {" · Chaired by "}{block.items[0].chair}
                                    </span>
                                  )}
                                </h2>
                              </div>
                            </div>
                          )}
                          <div className="space-y-2">
                            {block.items.map((s) => (
                              <SessionCard key={s.id} session={s} isFavorite={favorites.has(s.id)} onToggleFavorite={toggleFav} hideTime={showHeading} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
